import { API_BASE_URL } from '../../api/http';
import type { Endpoint } from '../../features/catalog/model';
import { CopyButton } from '../../components/CopyButton';
import { buildSnippets } from '../../lib/requestSnippets';
import { m } from '../../paraglide/messages';

export interface SkuCallCardProps {
  endpoint: Endpoint;
}

interface BlockProps {
  title: string;
  copyValue?: string;
  copyLabel?: string;
  children: string;
}

const Block = ({ title, copyValue, copyLabel, children }: BlockProps) => (
  <div className="border-t border-rule first:border-t-0">
    <div className="flex items-center justify-between gap-3 px-5 pt-3.5">
      <span className="mono text-12 text-text-3">{title}</span>
      {copyValue ? <CopyButton value={copyValue} label={copyLabel} /> : null}
    </div>
    <pre className="mono overflow-x-auto px-5 pb-4 pt-2.5 text-12 leading-[1.6] text-text-2">
      {children}
    </pre>
  </div>
);

/** Shows the real discovered operation, SKU, copy-paste cURL, and sample output. */
export const SkuCallCard = ({ endpoint }: SkuCallCardProps) => {
  const curl = endpoint.inputExample ? buildSnippets({
    baseUrl: API_BASE_URL,
    operation: endpoint,
    input: JSON.parse(endpoint.inputExample) as Record<string, unknown>,
  }).curl : null;

  return (
    <div className="overflow-hidden rounded-12 border border-rule bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule bg-wash px-5 py-3">
        <div className="flex items-center gap-2.5">
          <span className="mono text-12 text-text-3">SKU</span>
          <span className="mono rounded-6 border border-accent-line bg-accent-soft px-2 py-1 text-12 font-medium text-accent">
            {endpoint.sku}
          </span>
        </div>
        <CopyButton value={endpoint.sku} label={m.endpoint_copy_sku()} />
      </div>

      {curl ? (
        <Block title={`cURL · ${endpoint.method} ${endpoint.path}`} copyValue={curl} copyLabel={m.console_copy_language({ language: 'cURL' })}>
          {curl}
        </Block>
      ) : null}

      {endpoint.sampleResponse ? (
        <Block title={m.console_sample_response()} copyValue={endpoint.sampleResponse} copyLabel={m.console_copy_json()}>
          {endpoint.sampleResponse}
        </Block>
      ) : null}
    </div>
  );
};
